/**
 * CSV Export — generates downloadable CSV files from positions and orders.
 *
 * Pure functions for data → CSV string conversion,
 * plus a browser download trigger.
 */

import type { Position } from '@/types';
import type { KalshiOrder } from '@/lib/kalshiApi';

/** Escape a CSV field: wrap in quotes if it contains comma, quote, or newline */
function escapeField(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function toCsvRow(fields: (string | number | null | undefined)[]): string {
  return fields.map(escapeField).join(',');
}

/**
 * Export positions to CSV string.
 */
export function positionsToCsv(
  positions: Position[],
  titleByTicker?: Record<string, string>
): string {
  const headers = [
    'Ticker',
    'Market',
    'Side',
    'Quantity',
    'Avg Price (¢)',
    'Current Price (¢)',
    'Realized P&L ($)',
    'Unrealized P&L ($)',
    'Total P&L ($)',
    'P&L %',
  ];

  const rows = positions.map((p) =>
    toCsvRow([
      p.ticker,
      titleByTicker?.[p.ticker] ?? '',
      p.side,
      p.quantity,
      (p.averagePrice * 100).toFixed(1),
      p.currentPrice !== null ? (p.currentPrice * 100).toFixed(1) : '',
      p.realizedPnl.toFixed(2),
      p.unrealizedPnl.toFixed(2),
      p.totalPnl.toFixed(2),
      p.pnlPercent.toFixed(2),
    ])
  );

  return [toCsvRow(headers), ...rows].join('\n');
}

/**
 * Export open orders to CSV string.
 */
export function ordersToCsv(orders: KalshiOrder[]): string {
  const headers = [
    'Order ID',
    'Ticker',
    'Side',
    'Action',
    'Type',
    'Price (¢)',
    'Remaining',
    'Queue Position',
    'Status',
    'Created',
    'Expires',
  ];

  const rows = orders.map((o) => {
    const priceCents = o.side === 'yes' ? o.yes_price : o.no_price;
    return toCsvRow([
      o.order_id,
      o.ticker,
      o.side,
      o.action,
      o.type,
      priceCents ?? '',
      o.remaining_count,
      o.queue_position ?? '',
      o.status,
      o.created_time,
      o.expires_at ?? '',
    ]);
  });

  return [toCsvRow(headers), ...rows].join('\n');
}

/**
 * Trigger a browser file download from a CSV string.
 */
export function downloadCsv(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
